// ─────────────────────────────────────────────────────────────────────────────
// 🔑 VIEW OPENS THE REAL FILE IN THE APP'S VIEWER — NOT A BLOB, NOT window.open (Jesse, 2026-10-28).
//
// The server mints a five-minute link for this one document and this one borrower (POST /api/documents/:id/view-link
// → { path }); the link serves the real PDF / image with Content-Disposition inline and a filename, and stamps
// "first viewed" when it serves the bytes (backend/utils/documentViewLink). WHERE it opens is
// constants/documentViewerCore.chooseViewer:
//   · native + @capacitor/browser in the binary → the in-app browser at the real URL;
//   · native without it (iOS 1.0.2 today) → the app's own viewer route, /documents/view/:id (pages/DocumentViewer:
//     pdf.js inside the app, Back to Documents) — a navigation to the API URL would leave the app for Safari;
//   · web → a new tab at the real URL while the tap is a live gesture, else the in-app viewer.
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { chooseViewer } from '../constants/documentViewerCore';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const Browser = registerPlugin('Browser');

/** The signed URL for one document (the server decides access). */
export async function documentViewUrl(docId, token) {
  const r = await axios.post(`${API}/api/documents/${encodeURIComponent(docId)}/view-link`, {},
    { headers: { Authorization: `Bearer ${token}` } });
  const p = r && r.data && r.data.path;
  if (!p || !/^\/api\/documents\/open\//.test(p)) throw new Error('no view link');
  return `${API}${p}`;
}

// ── 🔑 THE LINK IS MINTED BEFORE THE TAP LANDS (2026-10-28) ────────────────────────────────────────────────
// A tab can be opened only while the tap is a live user gesture, and awaiting the mint on the click could outlive it
// (Safari allows about a second), sending a web borrower to the in-app viewer instead of the real PDF tab. So the
// link is minted when the pointer reaches View (hover, focus, touch, press) and the click opens the tab synchronously
// with it. A prepared link is used once and only while fresh (well inside its five minutes); otherwise the click mints
// as before. Minting stamps nothing — "first viewed" is still the GET that serves the bytes.
const PREPARED_TTL_MS = 4 * 60 * 1000;
const prepared = new Map();   // docId → { url, at, promise }

/** Mint a link ahead of the click (web only; native opens without a gesture deadline). */
export function prepareDocument(docId, token) {
  if (!docId || !token || Capacitor.isNativePlatform()) return;
  const e = prepared.get(docId);
  if (e && (e.promise || (e.url && Date.now() - e.at < PREPARED_TTL_MS))) return;
  const entry = { url: null, at: 0, promise: null };
  entry.promise = documentViewUrl(docId, token)
    .then((url) => { entry.url = url; entry.at = Date.now(); entry.promise = null; })
    .catch(() => { prepared.delete(docId); });
  prepared.set(docId, entry);
}

function takePrepared(docId) {
  const e = prepared.get(docId);
  if (!e || !e.url || Date.now() - e.at >= PREPARED_TTL_MS) return null;
  prepared.delete(docId);
  return e.url;
}

/** The route of the in-app viewer for a document. */
export const viewerPath = (docId) => `/documents/view/${encodeURIComponent(docId)}`;

function currentChoice() {
  const native = Capacitor.isNativePlatform();
  let override = null;
  try { override = window.__doorlyViewerOverride === 'in-app' ? 'in-app' : null; } catch (_) { override = null; }
  const ua = typeof navigator !== 'undefined' ? navigator.userActivation : null;
  return chooseViewer({ native, browserAvailable: native && Capacitor.isPluginAvailable('Browser'), userActive: !!(ua && ua.isActive), override });
}

function openNewTab(url) {
  const a = document.createElement('a');
  a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Open one document. doc: { id, name }; navigate: react-router's navigate (for the in-app viewer).
 * → 'browser' | 'in-app' | 'new-tab'. Throws when the server refuses the link.
 */
export async function openDocument(doc, token, navigate) {
  const goInApp = () => { navigate(viewerPath(doc.id), { state: { name: doc.name || null } }); return 'in-app'; };
  const choice = currentChoice();
  if (choice === 'in-app') return goInApp();
  // 🔑 Web with a link minted on the way to the tap: the tab opens now, inside the gesture, before any await.
  if (choice === 'new-tab') {
    const ready = takePrepared(doc.id);
    if (ready) { openNewTab(ready); return 'new-tab'; }
  }
  const url = await documentViewUrl(doc.id, token);
  if (choice === 'browser') {
    try { await Browser.open({ url, presentationStyle: 'popover' }); return 'browser'; }
    catch (_) { return goInApp(); }
  }
  // Web: the gesture may have expired while the link was minted — then the in-app viewer, never a blocked tab.
  const ua = typeof navigator !== 'undefined' ? navigator.userActivation : null;
  if (ua && !ua.isActive) return goInApp();
  openNewTab(url);
  return 'new-tab';
}
